/**
 * Ripia Auth Error Handling Utilities
 *
 * 認証エラーの処理・分類・リカバリ
 * @module auth/shared/errorHandling
 */

import { toast } from 'sonner';
import logger from '../../../utils/logger';
import { clearRipiaStorage } from './tokenStorage';
import { AuthError, type AuthErrorCode } from '../types';
import {
  SecurityErrorType,
  type MultiTenantApiError,
} from '../../../types/multiTenant';
import { SETUP_PENDING_CODES } from '../../../constants/errorCodes';

/**
 * Setup-pending エラー判定ヘルパー
 * @param errorData - APIエラーレスポンスデータ
 * @returns setup-pending状態であればtrue
 */
export const isSetupPendingError = (errorData: unknown): boolean => {
  const code = (errorData as { code?: string })?.code;
  return !!code && (SETUP_PENDING_CODES as readonly string[]).includes(code);
};

/**
 * 内部エラータイプ（ユーザー向けエラー分類）
 */
type InternalErrorType =
  | 'token_expired'
  | 'invalid_credentials'
  | 'permission_denied'
  | 'rate_limited'
  | 'network_error'
  | 'session_invalid'
  | 'device_mismatch'
  | 'mfa_required'
  | 'unknown';

/**
 * 401 Unauthorized エラーハンドリング
 * セッション期限切れ時にログイン画面へリダイレクト
 *
 * FIX Issue #930
 * @param errorData - APIエラーレスポンスデータ
 * @param context - エラー発生コンテキスト（ログ用）
 */
export const handleUnauthorizedError = (
  errorData: unknown,
  context: string
): void => {
  // Issue #2220: Webhook処理待ち（新規ユーザー登録直後）の場合はリダイレクトしない
  if (isSetupPendingError(errorData)) {
    logger.info(`[AuthService] Setup pending, skipping redirect - ${context}`, {
      errorData,
    });
    toast.info('アカウントを準備中です。しばらくお待ちください...', {
      id: 'setup-pending',
    });
    return;
  }

  // Issue #2318: Clerkセッションが有効ならリダイレクトしない（バックエンド側の問題）
  if (typeof window !== 'undefined') {
    const clerk = (window as unknown as { Clerk?: { session?: unknown } })
      .Clerk;
    if (clerk?.session) {
      logger.warn(
        `[AuthService] Clerk session still active, skipping redirect - ${context}`,
        { errorData }
      );
      toast.error(
        'サーバーとの通信に問題が発生しました。ページを再読み込みしてください。'
      );
      return;
    }
  }

  logger.warn(
    `[AuthService] セッション期限切れ (401 Unauthorized) - ${context}:`,
    errorData
  );

  // Ripia関連ストレージのみクリア
  clearRipiaStorage();

  // ログイン画面にリダイレクト（リダイレクトループ防止）
  // ブラウザ環境でのみリダイレクト（SSR対策）
  if (
    typeof window !== 'undefined' &&
    !window.location.pathname.startsWith('/login')
  ) {
    window.location.href = '/login?reason=session_expired';
  }
};

/**
 * エラーを内部タイプに分類
 * @param error - エラーオブジェクト
 * @returns InternalErrorType
 */
export const classifyError = (error: unknown): InternalErrorType => {
  if (error instanceof AuthError) {
    switch (error.code) {
      case 'TOKEN_EXPIRED':
      case 'SESSION_EXPIRED':
        return 'token_expired';
      case 'INVALID_CREDENTIALS':
      case 'AUTHENTICATION_FAILED':
        return 'invalid_credentials';
      case 'UNAUTHORIZED':
      case 'AUTHORIZATION_FAILED':
        return 'permission_denied';
      case 'RATE_LIMIT_EXCEEDED':
        return 'rate_limited';
      case 'NETWORK_ERROR':
      case 'TIMEOUT':
        return 'network_error';
      default:
        return 'unknown';
    }
  }

  if (error instanceof Error) {
    if (error.message.includes('network') || error.message.includes('fetch')) {
      return 'network_error';
    }
    if (error.message.includes('timeout')) {
      return 'network_error';
    }
  }

  return 'unknown';
};

/**
 * AuthErrorからSecurityErrorTypeへの変換
 * @param error - AuthError
 * @returns SecurityErrorType | undefined
 */
export const toSecurityErrorType = (
  error: AuthError
): SecurityErrorType | undefined => {
  switch (error.code) {
    case 'TOKEN_EXPIRED':
    case 'SESSION_EXPIRED':
      return SecurityErrorType.SESSION_EXPIRED;
    case 'INVALID_CREDENTIALS':
    case 'AUTHENTICATION_FAILED':
      return SecurityErrorType.AUTHENTICATION_FAILED;
    case 'UNAUTHORIZED':
    case 'AUTHORIZATION_FAILED':
      return SecurityErrorType.AUTHORIZATION_FAILED;
    case 'RATE_LIMIT_EXCEEDED':
      return SecurityErrorType.RATE_LIMIT_EXCEEDED;
    default:
      return undefined;
  }
};

/**
 * HTTPステータスコードからAuthErrorCodeへのマッピング
 * @param status - HTTPステータスコード
 * @returns AuthErrorCode
 */
export const mapStatusCodeToAuthErrorCode = (status: number): AuthErrorCode => {
  switch (status) {
    case 400:
      return 'VALIDATION_ERROR';
    case 401:
      return 'UNAUTHORIZED';
    case 403:
      return 'AUTHORIZATION_FAILED';
    case 404:
      return 'NOT_FOUND';
    case 409:
      return 'CONFLICT';
    case 429:
      return 'RATE_LIMIT_EXCEEDED';
    case 500:
      return 'SERVER_ERROR';
    case 503:
      return 'SERVICE_UNAVAILABLE';
    default:
      return 'NETWORK_ERROR';
  }
};

/**
 * 内部エラータイプに基づく推奨アクション
 * @param errorType - InternalErrorType
 * @returns アクション提案の配列
 */
export const getErrorSuggestions = (errorType: InternalErrorType): string[] => {
  switch (errorType) {
    case 'token_expired':
      return ['ページを再読み込みしてください', '再度ログインしてください'];
    case 'invalid_credentials':
      return [
        'メールアドレスとパスワードを確認してください',
        'パスワードをリセットしてください',
      ];
    case 'permission_denied':
      return ['この操作を行う権限がありません', '管理者にお問い合わせください'];
    case 'rate_limited':
      return [
        'しばらく待ってから再試行してください',
        '数分後に再度お試しください',
      ];
    case 'network_error':
      return [
        'インターネット接続を確認してください',
        'ページを再読み込みしてください',
      ];
    case 'session_invalid':
      return ['再度ログインしてください'];
    case 'device_mismatch':
      return [
        'このデバイスからのアクセスが認識されていません',
        '再度ログインして認証してください',
      ];
    case 'mfa_required':
      return ['二要素認証コードを入力してください'];
    default:
      return ['予期せぬエラーが発生しました', 'ページを再読み込みしてください'];
  }
};

/**
 * V2 APIレスポンスエラーハンドリング
 * @param response - Fetchレスポンス
 * @throws AuthError
 */
export const handleV2ResponseError = async (
  response: Response
): Promise<never> => {
  let errorData: { message?: string; code?: string } = {};

  try {
    errorData = await response.json();
  } catch (e) {
    logger.warn('Failed to parse response JSON:', e);
    errorData = { message: 'Response parse error' };
  }

  const errorCode = mapStatusCodeToAuthErrorCode(response.status);
  throw new AuthError(
    errorData.message || `HTTP ${response.status} エラー`,
    errorCode,
    response.status
  );
};

/**
 * 汎用V2 APIエラーハンドリング
 * @param error - 任意のエラー
 * @throws AuthError
 */
export const handleV2ApiError = (error: unknown): never => {
  if (error instanceof AuthError) {
    throw error;
  }

  if (error instanceof Error) {
    const errorType = classifyError(error);
    const errorCode =
      errorType === 'network_error' ? 'NETWORK_ERROR' : 'SERVER_ERROR';
    throw new AuthError(error.message, errorCode);
  }

  throw new AuthError('予期せぬエラーが発生しました', 'SERVER_ERROR');
};

/**
 * MultiTenantApiError作成ヘルパー
 * @param message - エラーメッセージ
 * @param statusCode - HTTPステータスコード
 * @returns MultiTenantApiError
 */
export const createMultiTenantError = (
  message: string,
  statusCode: number = 500
): MultiTenantApiError => ({
  statusCode,
  error: 'Error',
  message,
  timestamp: new Date().toISOString(),
  path: typeof window !== 'undefined' ? window.location.pathname : '/',
});

/**
 * エラーをMultiTenantApiErrorに変換
 * @param error - 任意のエラー
 * @returns MultiTenantApiError
 */
export const transformToMultiTenantError = (
  error: unknown
): MultiTenantApiError => {
  if (error instanceof AuthError) {
    return {
      statusCode: error.status || 500,
      error: error.code,
      message: error.message,
      timestamp: new Date().toISOString(),
      path: typeof window !== 'undefined' ? window.location.pathname : '/',
      details: {
        securityErrorType: toSecurityErrorType(error),
      },
    };
  }

  if (error instanceof Error) {
    return {
      statusCode: 500,
      error: 'UNKNOWN_ERROR',
      message: error.message,
      timestamp: new Date().toISOString(),
      path: typeof window !== 'undefined' ? window.location.pathname : '/',
    };
  }

  return {
    statusCode: 500,
    error: 'UNKNOWN_ERROR',
    message: '予期せぬエラーが発生しました',
    timestamp: new Date().toISOString(),
    path: typeof window !== 'undefined' ? window.location.pathname : '/',
  };
};

/**
 * 後方互換性のためのエイリアス
 * @deprecated Use classifyError instead
 */
export const classifySecurityError = classifyError;
